import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import classnames from 'classnames'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import {
  use3pdConfig,
  useFulfillment
} from 'src/public/components/online_ordering/FulfillmentContext'
import { usePayment } from 'src/public/components/online_ordering/PaymentContext'
import InputField from 'src/shared/components/common/form_input/InputField'
import FormattedPrice from 'src/shared/components/common/price/FormattedPrice'
import { useFormatPrice } from 'src/lib/js/hooks/useFormatPrice'
import { t } from 'src/translations'

import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection'
import { PreComputedTips } from 'public/components/online_ordering/types'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'
import { getUniqueTipOptions } from 'src/shared/js/utils/tipUtils'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import { Channel } from 'src/public/js/siteUtilities'
import { useA11y } from 'public/components/online_ordering/useA11y'

type Props = {
  tipOptions: PreComputedTips
  selectedTipButton: string
  onChange: (buttonId: string, tip: number, percent?: number) => void
}

export const OOTipSelector = (props: Props) => {
  const { tipOptions, selectedTipButton, onChange } = props
  const { getValues } = useFormContext()
  const { setTipAmount, tipEnabled } = usePayment()
  const { selectedDiningOptionBehavior } = useFulfillment()
  const customTipAmount = useWatch({
    name: 'custom-tip-amount',
    defaultValue: null
  })
  const { uses3pd } = use3pdConfig()

  const isDelivery =
    selectedDiningOptionBehavior === DiningOptionBehavior.Delivery
  const is3pdDelivery = isDelivery && uses3pd

  // tip options coming back from the consumer app bff may contain duplicates
  const uniqueTipOptions = useMemo(
    () => getUniqueTipOptions(tipOptions || []),
    [tipOptions]
  )

  const onClickCustomTip = () => {
    const tipValue = getValues('custom-tip-amount')
    if (tipValue === undefined || tipValue === null || tipValue === '') {
      onChange('custom-tip', 0.0)
    } else {
      const tipAmount = Number.parseFloat(tipValue)
      if (isNaN(tipAmount)) {
        onChange('custom-tip', 0.0)
      } else {
        onChange('custom-tip', Math.max(tipAmount, 0.0))
      }
    }
  }
  const onTipButtonClick = (
    buttonId: string,
    tipValue: number,
    tipPercent: number
  ) => onChange(buttonId, tipValue || 0.0, tipPercent)

  const validateCustomTip = (value: string) => {
    if (selectedTipButton !== 'custom-tip' || !value || value.trim() === '') {
      return true
    }
    const tipAmount = Number.parseFloat(value)
    if (isNaN(tipAmount)) {
      return t('tip-selector.please-enter-amount')
    }
    if (tipAmount < 0) {
      return t('tip-selector.cannot-have-negative-tip')
    }
    return true
  }

  useEffect(() => {
    if (!tipEnabled) {
      setTipAmount(0)
    } else if (customTipAmount !== null && customTipAmount !== undefined) {
      onChange('custom-tip', Number(customTipAmount))
    } else if (is3pdDelivery) {
      const tip = uniqueTipOptions.find((tip) => tip?.percent === 20)
      if (tip) {
        onChange(`tip-button-${tip.percent}`, tip.value)
      }
    } else if (!isDelivery) {
      // For non-delivery orders (pickup/takeout), ensure no tip is pre-selected
      setTipAmount(0)
    }
  }, [
    onChange,
    customTipAmount,
    uniqueTipOptions,
    tipEnabled,
    setTipAmount,
    is3pdDelivery,
    isDelivery
  ])

  const parsedCustomTip =
    customTipAmount != null ? Math.max(Number(customTipAmount), 0) : undefined

  return (
    <TipSelector
      tipOptions={uniqueTipOptions}
      selectedTipButton={selectedTipButton}
      onTipButtonClick={onTipButtonClick}
      onClickCustomTip={onClickCustomTip}
      customTipValue={parsedCustomTip}
      customTipInputComponent={
        <InputField
          id='custom-tip-amount'
          label={t('tip-selector.tip-amount')}
          type='number'
          step='any'
          min='0'
          validate={
            selectedTipButton === 'custom-tip' ? validateCustomTip : undefined
          }
          preventFormSubmit
        />
      }
    />
  )
}

type TipSelectorProps = {
  tipOptions: PreComputedTips
  selectedTipButton: string
  onTipButtonClick: (
    buttonId: string,
    tipValue: number,
    tipPercent: number
  ) => void
  onClickCustomTip: () => void
  customTipInputComponent: React.ReactNode
  customTipValue?: number
}

const CUSTOM_TIP_ANNOUNCE_DEBOUNCE_DELAY_MS = 1500

/**
 * This unifies the ecomm and OO tip sections so that they are consistent
 * @param tipOptions - precomputed tips
 * @param selectedTipButton - selected tip button
 * @param onTipButtonClick - on tip button click
 * @param onClickCustomTip - on custom tip click
 * @param customTipInputComponent - custom tip input component -- This is due to OO's input being part of a form and ecomm's is not
 * @param customTipValue - the current custom tip numeric value, used for debounced screen reader announcements
 */
const TipSelector = ({
  tipOptions,
  selectedTipButton,
  onTipButtonClick,
  onClickCustomTip,
  customTipInputComponent,
  customTipValue
}: TipSelectorProps) => {
  const { channel } = useCart()
  const { selectedDiningOptionBehavior } = useFulfillment()
  const { uses3pd } = use3pdConfig()
  const { announce } = useA11y()
  const isEcomm = channel === Channel.ECOMM

  const isDelivery =
    selectedDiningOptionBehavior === DiningOptionBehavior.Delivery
  const is3pdDelivery = isDelivery && uses3pd

  const isFullWidthCustomButton =
    useIsMobile(ScreenWidth.EXTRA_SMALL) || isEcomm
  const numTipOptions = Math.min(
    isFullWidthCustomButton ? tipOptions?.length : tipOptions?.length + 1,
    5
  )

  const [selectedTipValue, setSelectedTipValue] = useState<{
    value: number
    percent?: number
  } | null>(null)
  const formattedSelectedTipPrice = useFormatPrice(selectedTipValue?.value ?? 0)
  const formattedCustomTipPrice = useFormatPrice(customTipValue ?? 0)

  useEffect(() => {
    if (selectedTipValue?.percent != null) {
      announce(
        t('tip-selector.tip-selected', {
          percent: selectedTipValue.percent,
          amount: formattedSelectedTipPrice
        })
      )
    }
  }, [selectedTipValue, formattedSelectedTipPrice, announce])

  // Debounced announcement for custom tip changes
  useEffect(() => {
    if (selectedTipButton !== 'custom-tip') {
      return
    }
    if (customTipValue === undefined || customTipValue < 0) {
      return
    }
    const timer = setTimeout(() => {
      announce(
        t('tip-selector.custom-tip-added', { amount: formattedCustomTipPrice })
      )
    }, CUSTOM_TIP_ANNOUNCE_DEBOUNCE_DELAY_MS)

    return () => clearTimeout(timer)
  }, [customTipValue, selectedTipButton, formattedCustomTipPrice, announce])

  const tipLabel = is3pdDelivery
    ? t('tip-selector.add-driver-tip')
    : t('tip-selector.add-tip')

  return (
    <div className={classnames('tipSelector', isEcomm && 'ecomm')}>
      <div className='inputLabel'>
        <>
          {isEcomm && tipLabel}
          {is3pdDelivery && (
            <div className='tipSubheading'>
              {t('tip-selector.thank-your-driver')}
            </div>
          )}
        </>
      </div>
      <div className='tipButtons'>
        <div
          className='buttonGroup'
          style={{
            gridTemplateColumns: `repeat(${numTipOptions}, 1fr)`
          }}
          role='radiogroup'
          aria-label={tipLabel}
        >
          {tipOptions.map(
            (tip) =>
              tip && (
                <button
                  key={`tip-button-${tip.percent}`}
                  type='button'
                  role='radio'
                  aria-checked={
                    selectedTipButton === `tip-button-${tip.percent}`
                  }
                  data-testid={`tip-button-testid-${tip.percent}`}
                  className={classnames(
                    'tipButton',
                    selectedTipButton === `tip-button-${tip.percent}` &&
                      'selected'
                  )}
                  onClick={() => {
                    onTipButtonClick(
                      `tip-button-${tip.percent}`,
                      tip.value,
                      tip.percent
                    )
                    setSelectedTipValue({
                      value: tip.value,
                      percent: tip.percent
                    })
                  }}
                >
                  <div className='tipPercent'>{tip.percent}%</div>
                  <div className='tipAmount'>
                    <FormattedPrice value={tip.value} />
                  </div>
                </button>
              )
          )}
          <button
            type='button'
            role='radio'
            aria-checked={
              selectedTipButton === 'custom-tip' || !tipOptions?.length
            }
            className={classnames(
              'tipButton',
              (selectedTipButton === 'custom-tip' || !tipOptions?.length) &&
                'selected'
            )}
            style={
              isFullWidthCustomButton
                ? {
                    gridColumn: `span ${numTipOptions}`
                  }
                : {}
            }
            onClick={onClickCustomTip}
          >
            {isFullWidthCustomButton
              ? t('tip-selector.custom-mobile')
              : t('tip-selector.custom-desktop')}
          </button>
        </div>
        <AnimatedSection
          expanded={!tipOptions?.length || selectedTipButton === 'custom-tip'}
          className='customTipInput'
        >
          <div>{customTipInputComponent}</div>
        </AnimatedSection>
      </div>
    </div>
  )
}

export default TipSelector
